import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, ShieldCheck, ShieldAlert, AlertTriangle, ExternalLink,
  Loader2, Trash2, Info,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LicenceCheck — learner-side guided licence photo + OCR + manual confirm flow.
 * Clear framing: this is a guided admin support tool, not legal advice.
 * Nothing is marked verified until the learner manually confirms.
 */
export default function LicenceCheck() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [record, setRecord] = useState(null);
  const [stage, setStage] = useState("loading"); // loading | intro | uploaded | ocr | confirm | done
  const [uploading, setUploading] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState({
    surname: "", forenames: "", date_of_birth: "", issue_date: "",
    expiry_date: "", licence_number: "", licence_type: "provisional",
    dvla_check_code: "",
  });
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/licence/me`, { withCredentials: true });
      if (data.record) {
        setRecord(data.record);
        const cf = data.record.confirmed_fields || {};
        setFields((f) => ({
          ...f,
          ...cf,
          // Pre-fill from OCR if no confirmed values yet
          ...(data.record.ocr_extracted ? Object.fromEntries(Object.entries(data.record.ocr_extracted).filter(([k, v]) => v && !cf[k])) : {}),
          licence_type: (cf.licence_type) || (data.record.ocr_extracted?.licence_type) || "provisional",
        }));
        if (data.record.status === "verified") setStage("done");
        else if (data.record.ocr_extracted) setStage("confirm");
        else setStage("uploaded");
      } else {
        setStage("intro");
      }
    } catch {
      setStage("intro");
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/api/licence/upload`, fd, { withCredentials: true });
      setRecord(data);
      setStage("uploaded");
      toast.success("Photo uploaded — running OCR…");
      runOcr(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const runOcr = async (recId) => {
    setOcrRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/licence/ocr/${recId}`, {}, { withCredentials: true });
      setRecord(data);
      const ocr = data.ocr_extracted || {};
      setFields((f) => ({
        ...f,
        ...Object.fromEntries(Object.entries(ocr).filter(([, v]) => v)),
        licence_type: ocr.licence_type || f.licence_type || "provisional",
      }));
      setStage("confirm");
      toast.success("Details read — please check and correct anything.");
    } catch (e) {
      toast.error("OCR couldn't read the photo — you can still fill in your details manually.");
      setStage("confirm");
    } finally { setOcrRunning(false); }
  };

  const saveConfirm = async () => {
    if (!record) return;
    if (!fields.surname || !fields.licence_number || !fields.expiry_date) {
      toast.error("Please fill in at least surname, licence number and expiry date.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...fields };
      // Empty strings cause validation; drop them.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      const { data } = await axios.post(`${API}/api/licence/confirm/${record.id}`, payload, { withCredentials: true });
      setRecord(data);
      setStage("done");
      toast.success("Saved — your instructor can now see your licence status.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  const deleteRecord = async () => {
    if (!record) return;
    if (!confirm("Delete your licence record? You can upload again any time.")) return;
    try {
      await axios.delete(`${API}/api/licence/me/${record.id}`, { withCredentials: true });
      setRecord(null);
      setStage("intro");
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="licence-back-btn"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Licence Check</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Trust banner — always visible */}
        <div className="rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/40 p-3.5">
          <div className="flex items-start gap-2 text-blue-900 dark:text-blue-200">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
            <p className="text-xs leading-snug">
              This is a <span className="font-extrabold">guided admin support tool</span> to help your instructor stay organised. It's not legal advice or an official DVLA check. You will always be asked to confirm your details manually before anything is saved as verified.
            </p>
          </div>
        </div>

        {stage === "loading" && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {stage === "intro" && (
          <IntroCard
            onPick={(file) => upload(file)}
            uploading={uploading}
            fileRef={fileRef}
          />
        )}

        {(stage === "uploaded" || stage === "ocr") && (
          <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              {ocrRunning ? "Reading your licence…" : "Uploading…"}
            </p>
            <p className="text-xs text-slate-500 text-center">
              This takes a few seconds. You'll be asked to confirm every detail.
            </p>
          </div>
        )}

        {stage === "confirm" && (
          <ConfirmForm
            fields={fields}
            setFields={setFields}
            onSave={saveConfirm}
            onDelete={deleteRecord}
            saving={saving}
            photoUrl={record?.photo_url ? `${API}${record.photo_url}` : null}
          />
        )}

        {stage === "done" && record && (
          <DoneCard record={record} onRefresh={() => setStage("confirm")} onDelete={deleteRecord} />
        )}
      </div>
    </div>
  );
}

function IntroCard({ onPick, uploading, fileRef }) {
  return (
    <>
      <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 text-center space-y-4">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Camera className="w-7 h-7 text-blue-600 dark:text-blue-300" />
        </div>
        <div>
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">Take a photo of your licence</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Flat surface · good light · all four edges visible.
          </p>
        </div>
        <input
          ref={fileRef}
          data-testid="licence-file-input"
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
        />
        <button
          data-testid="licence-take-photo"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full px-4 py-3 text-sm font-extrabold rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60"
        >
          {uploading ? "Uploading…" : "Take / Upload photo"}
        </button>
      </div>

      <div className="rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-4">
        <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2">Privacy</p>
        <ul className="space-y-1.5 text-[11px] text-slate-600 dark:text-slate-400">
          <li>· Your photo is stored privately — only you, your linked instructor, and an admin on request can see it.</li>
          <li>· OCR reads the details automatically — you confirm them before anything is saved as verified.</li>
          <li>· You can delete your record at any time from this screen.</li>
        </ul>
      </div>
    </>
  );
}

function ConfirmForm({ fields, setFields, onSave, onDelete, saving, photoUrl }) {
  const set = (k) => (e) => setFields({ ...fields, [k]: e.target.value });

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 p-3.5">
        <div className="flex items-start gap-2 text-amber-900 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="text-xs leading-snug">
            <span className="font-extrabold">Please check every field.</span> We only save as verified after you manually confirm.
          </p>
        </div>
      </div>

      {photoUrl && (
        <img data-testid="licence-photo-preview" src={photoUrl} alt="Your licence" className="w-full rounded-2xl border border-slate-200 dark:border-slate-700" />
      )}

      <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 space-y-3">
        <Field label="Surname" testid="licence-surname" value={fields.surname} onChange={set("surname")} required />
        <Field label="Forenames" testid="licence-forenames" value={fields.forenames} onChange={set("forenames")} />
        <Field label="Date of birth" testid="licence-dob" value={fields.date_of_birth} onChange={set("date_of_birth")} placeholder="YYYY-MM-DD" />
        <Field label="Issue date (4a)" testid="licence-issue" value={fields.issue_date} onChange={set("issue_date")} placeholder="YYYY-MM-DD" />
        <Field label="Expiry date (4b)" testid="licence-expiry" value={fields.expiry_date} onChange={set("expiry_date")} placeholder="YYYY-MM-DD" required />
        <Field label="Licence number" testid="licence-number" value={fields.licence_number} onChange={(e) => setFields({ ...fields, licence_number: e.target.value.toUpperCase().replace(/\s+/g, "") })} required />
        <div>
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Licence type</label>
          <div className="mt-1 flex gap-2">
            {["provisional", "full"].map((opt) => (
              <button
                key={opt}
                data-testid={`licence-type-${opt}`}
                type="button"
                onClick={() => setFields({ ...fields, licence_type: opt })}
                className={`flex-1 px-3 py-2 text-sm font-bold rounded-lg border ${
                  fields.licence_type === opt
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"
                }`}
              >
                {opt === "provisional" ? "Provisional" : "Full"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-2">DVLA check code (optional)</p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">
          Generate a code on GOV.UK and paste it here so your instructor can run an official check.
        </p>
        <a
          href="https://www.gov.uk/view-driving-licence"
          target="_blank"
          rel="noreferrer"
          data-testid="licence-dvla-link"
          className="text-xs font-bold text-blue-600 hover:underline inline-flex items-center gap-1"
        >
          Open GOV.UK check service <ExternalLink className="w-3 h-3" />
        </a>
        <input
          data-testid="licence-check-code"
          className="mt-2 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 uppercase tracking-wider dark:text-white"
          placeholder="e.g. Ab12 Cd34 Ef56"
          value={fields.dvla_check_code || ""}
          onChange={(e) => setFields({ ...fields, dvla_check_code: e.target.value.toUpperCase() })}
        />
      </div>

      <button
        data-testid="licence-confirm-save"
        onClick={onSave}
        disabled={saving}
        className="w-full px-4 py-3 text-sm font-extrabold rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        <ShieldCheck className="w-4 h-4" /> {saving ? "Saving…" : "Confirm & save details"}
      </button>

      <button
        data-testid="licence-delete"
        onClick={onDelete}
        className="w-full px-4 py-2 text-xs font-bold rounded-xl bg-white dark:bg-slate-800 border border-red-200 dark:border-red-900/40 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center gap-1"
      >
        <Trash2 className="w-3 h-3" /> Delete and start over
      </button>
    </div>
  );
}

function DoneCard({ record, onRefresh, onDelete }) {
  const status = record.status;
  const cf = record.confirmed_fields || {};
  const expiryOk = status === "verified";
  const expiring = record.expiry_date && (() => {
    try {
      const d = new Date(record.expiry_date);
      return (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24) <= 90;
    } catch { return false; }
  })();

  return (
    <div className="space-y-3">
      <div
        data-testid="licence-done-card"
        className={`rounded-2xl border p-5 text-center ${
          expiryOk
            ? "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40"
            : "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-900/40"
        }`}
      >
        {expiryOk ? (
          <ShieldCheck className="w-10 h-10 mx-auto text-emerald-600" />
        ) : (
          <ShieldAlert className="w-10 h-10 mx-auto text-amber-600" />
        )}
        <p className={`mt-2 text-lg font-extrabold ${expiryOk ? "text-emerald-900 dark:text-emerald-100" : "text-amber-900 dark:text-amber-100"}`}>
          {expiryOk ? "Licence verified" : status === "expired" ? "Licence expired" : "Awaiting confirmation"}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {cf.surname || ""} {cf.forenames || ""} · {cf.licence_number || record.licence_number || "—"}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">Expires {record.expiry_date || "—"}</p>
        {expiring && (
          <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300 font-bold">
            Expires within 90 days — worth renewing.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <button
          data-testid="licence-refresh"
          onClick={onRefresh}
          className="flex-1 px-3 py-2 text-xs font-bold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        >
          Update details
        </button>
        <button
          data-testid="licence-delete-done"
          onClick={onDelete}
          className="px-3 py-2 text-xs font-bold rounded-lg bg-white dark:bg-slate-800 border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        data-testid={testid}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white"
      />
    </div>
  );
}
